<template>
  <div style="position: absolute; left: -9999px; top: -9999px">
    <div
      ref="receiptWrapper"
      class="receipt-print-container bg-white"
      style="width: 297mm; height: 210mm; padding: 10mm; color: black; font-family: 'Arial', sans-serif; box-sizing: border-box;"
    >
      <div class="flex gap-4 h-full">
        <!-- Loop for two copies -->
        <div v-for="copy in ['Customer Copy', 'Official Copy']" :key="copy" class="flex-1 flex flex-col h-full border-r last:border-r-0 border-gray-200">
          <div class="receipt-box border-2 border-black p-4 relative flex-1 flex flex-col bg-white" style="box-sizing: border-box; overflow: hidden;">
            <div class="text-[10px] italic absolute top-1 left-2 font-bold">{{ copy }}</div>
            
            <!-- Header -->
            <div class="text-center mb-6 border-b border-gray-200 pb-3">
              <h1 class="text-xl font-bold uppercase tracking-tight">Zavron Finance Pvt Ltd</h1>
              <p class="text-[11px] leading-tight px-12 mt-1">125, Zavron finance pvt ltd near mure memorial hospital maharajbagh road, sitabuldi, Nagpur, Maharashtra</p>
              <p class="text-[11px] font-bold mt-1">Branch: NAGPUR . Ph: 0712-2520007</p>
            </div>

            <!-- Details Grid -->
            <div class="grid grid-cols-2 gap-x-12 gap-y-3 text-[13px] mb-6 px-2">
              <div class="flex border-b border-gray-100 pb-1"><span class="font-bold w-28 text-gray-700">Date:</span> <span class="font-medium">{{ formatDate(payment.date) }}</span></div>
              <div class="flex border-b border-gray-100 pb-1"><span class="font-bold w-32 text-right pr-6 text-gray-700">Voucher No:</span> <span class="font-medium">{{ payment.voucherNo || payment.receiptNo || payment.transactionId || 'HOR0000000' }}</span></div>
              
              <div class="flex col-span-2 border-b border-gray-100 pb-1 mt-1"><span class="font-bold w-28 text-gray-700">Customer:</span> <span class="font-bold uppercase text-lg text-black">{{ customerName }}</span></div>
              
              <div class="flex col-span-2 border-b border-gray-100 pb-1 mt-1"><span class="font-bold w-28 text-gray-700">Instrument No:</span> <span class="font-medium">{{ payment.utrNo || payment.chequeNo || '0' }}</span></div>
              
              <div class="flex mt-1 border-b border-gray-100 pb-1"><span class="font-bold w-28 text-gray-700">Ag. Date:</span> <span class="font-medium">{{ formatDate(agreementDate) }}</span></div>
              <div class="flex mt-1 border-b border-gray-100 pb-1"><span class="font-bold w-32 text-right pr-6 text-gray-700">Ag. No:</span> <span class="font-medium">{{ agreementNo }}</span></div>
            </div>

            <!-- Items Table -->
            <table class="w-full text-[13px] border-collapse mb-8 mt-5 px-2">
              <thead>
                <tr class="text-left border-y-2 border-black bg-gray-50">
                  <th class="py-2 px-2">#</th>
                  <th class="py-2 px-2">Due Date</th>
                  <th class="py-2 px-2 text-right w-1/3">Desc</th>
                  <th class="py-2 px-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr class="font-semibold text-black">
                  <td class="py-2 px-2 border-b border-gray-100">(Part) {{ emiNo }} / {{ totalEmis }}</td>
                  <td class="py-2 px-2 border-b border-gray-100">{{ formatDate(emiDueDate) }}</td>
                  <td class="text-right py-2 px-2 border-b border-gray-100">EMI</td>
                  <td class="text-right py-2 px-2 border-b border-gray-100">{{ formatCurrency(baseAmount) }}</td>
                </tr>
                <tr v-if="payment.latePaymentAmount > 0">
                  <td colspan="2" class="px-2"></td>
                  <td class="text-right py-1.5 px-2 text-gray-700 italic">*LP Charges</td>
                  <td class="text-right py-1.5 px-2 text-gray-700">{{ formatCurrency(payment.latePaymentAmount) }}</td>
                </tr>
                <tr v-if="payment.collectionCharges > 0">
                  <td colspan="2" class="px-2"></td>
                  <td class="text-right py-1.5 px-2 text-gray-700 italic">Collection Charges</td>
                  <td class="text-right py-1.5 px-2 text-gray-700">{{ formatCurrency(payment.collectionCharges) }}</td>
                </tr>
              </tbody>
              <tfoot class="border-t-2 border-black bg-gray-50">
                <tr class="font-bold text-black text-sm">
                  <td colspan="2" class="px-2"></td>
                  <td class="text-right py-3 px-2">Total</td>
                  <td class="text-right py-3 px-2">₹{{ formatCurrency(payment.amount) }}</td>
                </tr>
              </tfoot>
            </table>

            <!-- Bottom Section -->
            <div class="grid grid-cols-2 text-[14px] mt-6 px-2 gap-4">
              <div class="flex"><span class="font-bold mr-6 text-gray-700">Payment Mode:</span> <span class="font-medium">{{ payment.paymentMode || 'Cash' }}</span></div>
              <div class="flex justify-end"><span class="font-bold mr-6 text-gray-700">Cashier:</span> <span class="font-medium underline decoration-dotted">{{ cashierName || 'Bharat Vaswani' }}</span></div>
            </div>

            <!-- Signatures -->
            <div class="flex justify-between items-end mt-10 text-[12px] px-2 pb-5">
              <div class="border-t-2 border-black pt-2 w-48 text-center italic font-medium">Customer Signature</div>
              <div class="text-right">
                <p class="mb-8 font-bold uppercase tracking-tight">For Zavron Finance Pvt Ltd</p>
                <div class="w-32 ml-auto border-t border-gray-400 pt-1 text-center italic text-gray-600">Cashier</div>
              </div>
            </div>

            <!-- Footer Text -->
            <div class="absolute bottom-2 left-6 right-6 flex justify-between items-center text-[10px] text-gray-400 border-t border-gray-100 pt-2">
               <div>Created Date : {{ formatDateTime(payment.createdAt || payment.date) }}</div>
               <div class="font-bold italic text-gray-500">This is System Generated Receipt.</div>
               <div>Printed: {{ formatDateTime(now) }}</div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmiPaymentReceipt',
  props: {
    payment: { type: Object, required: true },
    customerName: { type: String, default: 'Customer' },
    agreementDate: { type: String, default: '' },
    agreementNo: { type: String, default: '' },
    emiNo: { type: Number, default: 1 },
    totalEmis: { type: Number, default: 0 },
    emiDueDate: { type: String, default: '' },
    emiAmount: { type: Number, default: 0 },
    cashierName: { type: String, default: '' }
  },
  data() {
    return {
      now: new Date()
    }
  },
  computed: {
    baseAmount() {
      const total = parseFloat(this.payment.amount) || 0
      const lp = parseFloat(this.payment.latePaymentAmount) || 0
      const cc = parseFloat(this.payment.collectionCharges) || 0
      return total - lp - cc
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return '-'
      return this.$dayjs(date).format('DD-MM-YYYY')
    },
    formatDateTime(date) {
      if (!date) return '-'
      return this.$dayjs(date).format('DD-MM-YYYY HH:mm:ss')
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0)
    },
    getHtmlElement() {
      return this.$refs.receiptWrapper
    }
  }
}
</script>

<style scoped>
@media print {
  .receipt-print-container {
    padding: 0 !important;
  }
}
</style>
